
import { defineComponent } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import Loader from '@/components/Loader.vue';
import { computed, markRaw } from '@vue/runtime-core';
import Utils from '@/utils/QSUtils';
import { useStore } from 'vuex';
import DoubleIndex from '@/models/DoubleIndex';
import { Actions, Getters, store } from '@/store';

import ModalConfirmation from '@/components/modals/Confirmation.vue';
import GuideManager from '@/manager/GuideManager';
import IComp from '@/models/Comp';
import LogUsageManager from '@/manager/LogUsageManager';
import IComps from '@/models/comp/IComps';
import CompsManager from '@/manager/3.6/CompsManager';

let dropIndex = 0;
export default defineComponent({
    components: {
        draggable: VueDraggableNext,
        Loader,
        ModalConfirmation,
    },
    data() {
        return {
            isDragging: false,
            modalConfirmation: {
                show: false,
                component: markRaw(ModalConfirmation),
                message: '',
                size: '',
                buttons: [] as any,
            },
        };
    },
    computed: {
        dragOptions() {
            return {
                animation: 0,
                group: 'description',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
    },
    mounted() {
        (this as any).eventBus.emit('checkTabOverflow');
        window.addEventListener('resize', () => {
            (this as any).eventBus.emit('checkTabOverflow');
        });
    },
    setup() {
        const store = useStore();
        let compsData = computed(() => store.getters[Getters.CompAddressesV36.Comps] as IComps);

        return { compsData };
    },
    methods: {
        async selectComp(tabIndex: number, isLoading: boolean) {
            if (isLoading) return;

            if (Utils.isNumber(Number(tabIndex))) {
                GuideManager.guideCompleted(6);
                CompsManager.setActiveComp(tabIndex);

                (this as any).eventBus.emit('resetGridHeaderElements');
            }
        },
        async deleteComp(tabIndex: number, isLoading: boolean) {
            if (isLoading) return;
            if (Utils.isNumber(Number(tabIndex))) {
                await CompsManager.deleteComp(tabIndex);
            }
        },
        moveCompLeft(tabIndex: number, isLoading: boolean) {
            if (isLoading) return;
            const compsData = computed(() => store.getters[Getters.CompAddressesV36.Comps] as IComps);
            if (Utils.isNumber(Number(tabIndex)) && tabIndex > 1) {
                const indexes: DoubleIndex = {
                    source: Number(tabIndex),
                    target: Number(tabIndex) - 1,
                };
                const comp: IComp = compsData.value[indexes.source];
                LogUsageManager.rearrangedCompTabs(comp.address, indexes, false);
                store.dispatch(Actions.CompAddressesV36.MoveComp, indexes);
            }
        },
        moveCompRight(tabIndex: number, isLoading: boolean) {
            if (isLoading) return;
            const compsData = computed(() => store.getters[Getters.CompAddressesV36.Comps] as IComps);
            if (Utils.isNumber(Number(tabIndex)) && tabIndex < compsData.value.length - 1) {
                const indexes: DoubleIndex = {
                    source: Number(tabIndex),
                    target: Number(tabIndex) + 1,
                };
                const comp: IComp = compsData.value[indexes.source];
                LogUsageManager.rearrangedCompTabs(comp.address, indexes, false);
                store.dispatch(Actions.CompAddressesV36.MoveComp, indexes);
            }
        },
        onCompDrop(event: any, isLoading: boolean) {
            if (isLoading) return;
            const indexes: DoubleIndex = {
                source: Number(event.moved.oldIndex),
                target: dropIndex,
            };
            const compsData = computed(() => store.getters[Getters.CompAddressesV36.Comps] as IComps);
            const comp: IComp = compsData.value[indexes.source];
            LogUsageManager.rearrangedCompTabs(comp.address, indexes, true);
            store.dispatch(Actions.CompAddressesV36.MoveComp, indexes);
        },
        tabAction(event: any, index: number, isLoading: boolean) {
            const element = event.target.closest('[data-action]');
            if (element) {
                const attr = element.getAttribute('data-action');
                switch (attr) {
                    case 'select-tab':
                        this.selectComp(index, isLoading);
                        break;
                    case 'move-left':
                        this.moveCompLeft(index, isLoading);
                        break;
                    case 'move-right':
                        this.moveCompRight(index, isLoading);
                        break;
                    case 'delete-tab':
                        this.deleteConfirmation(index);
                        break;
                    case 'move-tab':
                        // Is handled within the VueDraggableNext
                        break;
                }
            } else {
                console.log('not found');
            }
        },
        compNumber(compNumber: number): string {
            return compNumber === 0 ? 'Subject' : `Comp ${compNumber}`;
        },
        hasCompViewed(hasVisited: boolean): string {
            return hasVisited ? 'fas fa-check-circle p-color-2' : 'fas fa-exclamation-triangle s-color-3';
        },
        setTabFocus(isActive: boolean): string {
            return isActive ? 'comp-tab-active' : '';
        },
        isLoading(isLoading: boolean) {
            return isLoading ? 'comp-tab-loading' : '';
        },
        scrollLeft(): void {
            const compContainer = this.$refs.compContainer as HTMLElement;
            compContainer.scrollLeft -= 200;
        },
        scrollRight(): void {
            const compContainer = this.$refs.compContainer as HTMLElement;
            compContainer.scrollLeft += 200;
        },
        // todo: specify event type
        onMoveCompCheck(event: any) {
            if (event.draggedContext.index === 0 || event.relatedContext.index === 0) {
                return false;
            } else {
                dropIndex = Number(event.relatedContext.index);
                return true;
            }
        },
        deleteConfirmation(idx: number) {
            this.modalConfirmation.show = true;
            this.modalConfirmation.message = `<div>Are you sure you want to remove <b>Comp ${this.compsData[idx].compNumber}</b> from the report?</div>
                                              <div class="font-weight-bold mt-1">${this.compsData[idx].address.address1}</div>
                                              <div class="font-weight-bold mt-1">${this.compsData[idx].address.city}, ${this.compsData[idx].address.state} ${this.compsData[idx].address.zipCode}</div>`;
            this.modalConfirmation.buttons = [
                {
                    label: 'Yes',
                    css: 'btn-primary',
                    callback: () => {
                        this.deleteComp(idx, false);
                        this.selectComp(idx - 1, false);
                    },
                },
                {
                    label: 'Cancel',
                    css: 'btn-outline-secondary',
                },
            ];
        },
    },
});
